import React, { useMemo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { ModalContextProvider } from '@atlassian/entry-point-modal-context/ModalContextProvider';
import { BFFConsumerEnum, BFFProvider } from '@atlassian/navigation-bff-data-provider';

import { withSideNavigationAnalyticsContext } from '../common/analytics/context';
import type { GlobalSideNavigationProps } from '../common/types';
import { AsyncIntlProvider } from '../common/utils/locale';
import { ItemsAndShortcutsProvider } from '../controllers/items-and-shortcuts-provider';
import { OptionsProvider } from '../controllers/options-provider';
import { PeekMenuIdProviders } from '../controllers/peek-menu-id-providers';

import { LocalStore } from './side-nav/LocalStore';
import { NetworkMigrationStore } from './side-nav/NetworkMigrationStore';
import { NetworkStore } from './side-nav/NetworkStore';
import { SideNav } from './side-nav/SideNav';

export const GlobalSideNavigation = withSideNavigationAnalyticsContext(RawGlobalSideNavigation);

export default RawGlobalSideNavigation; // This is only needed to support the Atlaskit docs. See https://hello.atlassian.net/wiki/spaces/navx/pages/5305993008/Platform+Guide#Atlaskit-%3CProps%3E-component-not-rendering

function RawGlobalSideNavigation(props: GlobalSideNavigationProps): React.JSX.Element {
	const {
		app,
		appLabel,
		cloudId,
		customizationStore = 'local',
		disableMenuCustomization = false,
		forYou,
		growthItemShortcuts,
		isShortcutsStatic,
		items,
		networkStorageParams,
		orgId,
		peekMenuId,
		shortcuts,
		userId,
		...innerProps
	} = props;

	const Store = useMemo(() => {
		switch (customizationStore) {
			case 'local':
				return LocalStore;
			case 'network-migration':
				return NetworkMigrationStore;
			default:
				return NetworkStore;
		}
	}, [customizationStore]);

	return (
		<AsyncIntlProvider>
			<ModalContextProvider>
				<BFFProvider consumer={BFFConsumerEnum.GLOBAL_SIDE_NAVIGATION}>
					<OptionsProvider
						app={app}
						appLabel={appLabel}
						cloudId={cloudId}
						customizationStore={customizationStore}
						disableMenuCustomization={disableMenuCustomization}
						isShortcutsStatic={isShortcutsStatic}
						l1DragAndDropEnabled={
							!disableMenuCustomization &&
							(fg('navx-515-l1-dnd-support') || fg('focus-l1-dnd-support'))
						}
						networkStorageParams={networkStorageParams}
						orgId={orgId}
						userId={userId}
						hasBottomElement={!!innerProps?.bottomElem}
					>
						<ItemsAndShortcutsProvider
							forYou={forYou}
							growthItemShortcuts={growthItemShortcuts}
							items={items}
							rawShortcuts={shortcuts}
						>
							<PeekMenuIdProviders value={peekMenuId}>
								<Store>
									<SideNav {...innerProps} />
								</Store>
							</PeekMenuIdProviders>
						</ItemsAndShortcutsProvider>
					</OptionsProvider>
				</BFFProvider>
			</ModalContextProvider>
		</AsyncIntlProvider>
	);
}
